import { Col, Form, Input, Row, Select } from "antd"
import { useEffect, useMemo } from "react"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import DisabledFieldTooltip from "#blocks/Form/DisabledFieldTooltip"
import { NULL_ID } from "#classes/Firebase"
import SemesterDropdown from "#components/forms/SemesterDropdown"
import SkillLevelDropdown from "#components/forms/SkillLevelDropdown"
import { CLASS_GROUP_OPTIONS, CLASS_GROUPS } from "#constants/classOptions"
import { PROJECT_MEDIA_OPTIONS } from "#constants/projectOptions"
import { CLASS_GROUP_TO_SKILL_TYPE } from "#constants/skillLevelConstants"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { type ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { CheckUsageQuery, useCheckUsage } from "#hooks/reactQuery/useCheckUsage"

const AddProjectForm = ({
  initialValues,
}: {
  // Create mode passes only `{ classGroup }` so the skill-level reset can
  // compare against the stub. Edit mode passes the saved project.
  initialValues: ProjectConstructor | { classGroup: CLASS_GROUPS } | undefined
}) => {
  const form = Form.useFormInstance<ProjectConstructor>()
  const classGroup =
    Form.useWatch("classGroup", form) || initialValues?.classGroup

  useEffect(() => {
    if (
      initialValues?.classGroup &&
      CLASS_GROUP_TO_SKILL_TYPE[classGroup] !==
        CLASS_GROUP_TO_SKILL_TYPE[initialValues.classGroup]
    ) {
      form.setFieldValue("skillLevels", [])
    }
  }, [classGroup, form, initialValues?.classGroup])

  const projectId =
    initialValues != null && "id" in initialValues ? initialValues.id : NULL_ID
  const queries: CheckUsageQuery[] = useMemo(
    () => [
      {
        docType: DOC_TYPES.CURRICULUMS,
        queries: {
          projectIds: {
            value: projectId,
            comparator: COMPARATORS.CONTAINS,
          },
        },
      },
      {
        docType: DOC_TYPES.STUDENTS,
        queries: {
          [`projects.${projectId}`]: {
            value: null,
            comparator: COMPARATORS.DOES_NOT_EQUAL,
          },
        },
      },
    ],
    [projectId]
  )
  const { data: isUsed } = useCheckUsage(DOC_TYPES.PROJECTS, projectId, queries)

  return (
    <>
      <Row gutter={16}>
        <Col span={14}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Please input the Project plan name!",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item
                name="medium"
                label="Medium"
                rules={[{ required: true, message: "Please select a medium!" }]}
              >
                <Select options={PROJECT_MEDIA_OPTIONS} />
              </Form.Item>
            </Col>

            <Col span={6}>
              <SemesterDropdown
                formItemProps={{
                  required: true,
                  rules: [
                    { required: true, message: "Please select a semester!" },
                  ],
                }}
                disabledTooltip={
                  isUsed
                    ? "Cannot change semester - this project is currently being used by curriculum plans"
                    : undefined
                }
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="classGroup"
                label="Class Group"
                rules={[
                  { required: true, message: "Please select a class group!" },
                ]}
              >
                <Select options={CLASS_GROUP_OPTIONS} />
              </Form.Item>
            </Col>
            <AnimateInFlex animation={ANIMATION_BY_KEY.leftSm} grow>
              <Col span={18}>
                <SkillLevelDropdown
                  multiSelect
                  required
                  classGroup={classGroup}
                />
              </Col>
            </AnimateInFlex>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <DisabledFieldTooltip
                disabled={isUsed}
                tooltip={
                  isUsed
                    ? "Cannot change days to complete - this project is currently being used by curriculums"
                    : undefined
                }
              >
                <Form.Item
                  name="daysToComplete"
                  label="Days to complete"
                  rules={[
                    {
                      required: true,
                      message:
                        "Please input how many days this will take to complete!",
                    },
                  ]}
                >
                  <Input type="number" disabled={isUsed} />
                </Form.Item>
              </DisabledFieldTooltip>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="link" label="Link" rules={[{ required: false }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: false }]}
              >
                <Input.TextArea rows={10} />
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col span={10}>
          <Form.Item name="notes" label="Notes" rules={[{ required: false }]}>
            <Input.TextArea rows={18} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="id" style={{ display: "none" }}>
        <Input type="hidden" />
      </Form.Item>
    </>
  )
}

export default AddProjectForm
